import type { HrCityId, HrCompanyId } from '../constants/hr-onboarding.constants';
import { HR_COMPANY_BY_ID, isHrCityId, isHrCompanyId } from '../constants/hr-onboarding.constants';
import { normalizeHrMailingGroups, type HrMailingGroupId } from '../constants/hr-mailing-groups.constants';
import { normalizeCzechKraj } from '../constants/user-hr-kraj.constants';

export interface UserHrContractFields {
  cityId?: HrCityId | null;
  address?: string | null;
  nastup?: string | null;
  rodneCislo?: string | null;
  mistoNarozeni?: string | null;
  cisloOp?: string | null;
  ucet?: string | null;
  druhPrace?: string | null;
  pojistovna?: string | null;
  mzda?: string | null;
  narozeniny?: string | null;
}

/** Raw contract fields from API/DB before normalization. */
export type UserHrContractFieldsInput = Omit<UserHrContractFields, 'cityId'> & {
  cityId?: string | null;
};

export interface UserHrProfileFields {
  hrCompanyId?: HrCompanyId | null;
  phone?: string | null;
  altPhone?: string | null;
  jobTitle?: string | null;
  /** Kraj (administrativní). */
  kraj?: string | null;
  /** Pobočka / provoz (např. ProTelco Lovosice). */
  region?: string | null;
  mailingGroups?: HrMailingGroupId[];
  altEmails?: string[];
  hrDriveFolderId?: string | null;
  contractFields?: UserHrContractFields;
}

/** Raw HR profile payload before normalization (loose ids from API/DB). */
export type UserHrProfileFieldsInput = Omit<UserHrProfileFields, 'hrCompanyId' | 'contractFields'> & {
  hrCompanyId?: string | null;
  contractFields?: Partial<UserHrContractFieldsInput> | null;
};

export function normalizeUserAltEmails(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    const email = String(raw ?? '').trim().toLowerCase();
    if (!email.includes('@') || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out.sort();
}

function trimOrNull(value: unknown, maxLen?: number): string | null {
  let s = String(value ?? '').trim();
  if (!s) return null;
  if (maxLen != null && s.length > maxLen) s = s.slice(0, maxLen);
  return s;
}

export function normalizeUserHrContractFields(
  input: Partial<UserHrContractFieldsInput> | null | undefined,
): UserHrContractFields {
  const raw = input && typeof input === 'object' ? input : {};
  const rawCity = raw.cityId as string | null | undefined;
  const cityId =
    rawCity === null || rawCity === undefined || String(rawCity).trim() === ''
      ? null
      : isHrCityId(rawCity)
        ? rawCity
        : null;
  const mzdaRaw = raw.mzda;
  const mzda =
    mzdaRaw === null || mzdaRaw === undefined || String(mzdaRaw).trim() === ''
      ? null
      : String(mzdaRaw).trim();
  return {
    cityId,
    address: trimOrNull(raw.address, 512),
    nastup: trimOrNull(raw.nastup, 32),
    rodneCislo: trimOrNull(raw.rodneCislo, 32),
    mistoNarozeni: trimOrNull(raw.mistoNarozeni, 128),
    cisloOp: trimOrNull(raw.cisloOp, 32),
    ucet: trimOrNull(raw.ucet, 64),
    druhPrace: trimOrNull(raw.druhPrace, 255),
    pojistovna: trimOrNull(raw.pojistovna, 32),
    mzda,
    narozeniny: trimOrNull(raw.narozeniny, 32),
  };
}

export function normalizeUserHrProfile(input: Partial<UserHrProfileFieldsInput>): UserHrProfileFields {
  const rawCompany = input.hrCompanyId as string | null | undefined;
  const hrCompanyId =
    rawCompany === null || rawCompany === undefined || String(rawCompany).trim() === ''
      ? null
      : isHrCompanyId(rawCompany)
        ? rawCompany
        : null;
  const phone = trimOrNull(input.phone, 64);
  const altPhone = trimOrNull(input.altPhone, 64);
  const jobTitle = trimOrNull(input.jobTitle, 255);
  const kraj = normalizeCzechKraj(input.kraj);
  const region = trimOrNull(input.region, 128);
  const hrDriveFolderId = trimOrNull(input.hrDriveFolderId, 128);
  return {
    hrCompanyId,
    phone,
    altPhone,
    jobTitle,
    kraj,
    region,
    mailingGroups: normalizeHrMailingGroups(input.mailingGroups ?? []),
    altEmails: normalizeUserAltEmails(input.altEmails ?? []),
    hrDriveFolderId,
    contractFields: normalizeUserHrContractFields(input.contractFields),
  };
}

export function hrCompanyDisplayName(companyId: HrCompanyId | null | undefined): string | null {
  if (!companyId || !isHrCompanyId(companyId)) return null;
  return HR_COMPANY_BY_ID[companyId]?.name ?? null;
}

export function formatHrMailingGroupLabels(groups: HrMailingGroupId[] | undefined): string {
  if (!groups?.length) return '—';
  return groups.join(', ');
}
